/**
 * 统一带登录态的请求：401 时尝试 refresh 并重试一次。
 * 供 api.ts、上传链路、专辑提交等长流程复用。
 */

import { AUDIO_UPLOAD_MAX_LABEL } from './uploadLimits';

export const SESSION_EXPIRED_MESSAGE = '登录已过期，请重新登录后继续操作';
export const SESSION_BUSY_MESSAGE = '当前操作较频繁，请稍等片刻后再试';

const ACCESS_EXPIRES_KEY = 'accessTokenExpiresIn';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export function computeProactiveRefreshDelayMs(expiresInSeconds?: number): number {
  const fromArg = expiresInSeconds;
  const stored = parseInt(localStorage.getItem(ACCESS_EXPIRES_KEY) || '', 10);
  const sec =
    (typeof fromArg === 'number' && fromArg > 0 ? fromArg : null) ??
    (Number.isFinite(stored) && stored > 0 ? stored : null) ??
    7 * 24 * 3600;
  const halfSec = Math.floor(sec * 0.5);
  const minMs = 2 * 60 * 1000;
  const maxMs = 30 * 60 * 1000;
  return Math.min(maxMs, Math.max(minMs, halfSec * 1000));
}

export function cancelProactiveRefresh(): void {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

export function scheduleProactiveRefresh(expiresInSeconds?: number): void {
  if (typeof window === 'undefined') return;
  if (!localStorage.getItem('refreshToken')) return;

  cancelProactiveRefresh();
  const delay = computeProactiveRefreshDelayMs(expiresInSeconds);
  proactiveRefreshTimer = setTimeout(() => {
    void refreshAccessToken().then((token) => {
      if (token) scheduleProactiveRefresh();
    });
  }, delay);
}

export function persistAuthTokens(tokens: {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
}): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('accessToken', tokens.accessToken);
  if (tokens.refreshToken) {
    localStorage.setItem('refreshToken', tokens.refreshToken);
  }
  if (typeof tokens.expiresIn === 'number' && tokens.expiresIn > 0) {
    localStorage.setItem(ACCESS_EXPIRES_KEY, String(tokens.expiresIn));
  }
  scheduleProactiveRefresh(tokens.expiresIn);
}

export function clearAuthTokens(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(ACCESS_EXPIRES_KEY);
  cancelProactiveRefresh();
}

export async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  if (typeof window === 'undefined') {
    return null;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return null;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.status === 429) {
        return null;
      }

      if (response.status === 401) {
        clearAuthTokens();
        return null;
      }

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      if (data.success && data.accessToken) {
        persistAuthTokens({
          accessToken: data.accessToken as string,
          expiresIn:
            typeof data.expiresIn === 'number' ? data.expiresIn : undefined,
        });
        return data.accessToken as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export type AuthenticatedFetchOptions = {
  /** 为 false 时不在 401 后自动 refresh（用于已重试过的请求） */
  allowAuthRetry?: boolean;
};

function withAccessTokenHeader(init: RequestInit): RequestInit {
  if (typeof window === 'undefined') {
    return init;
  }
  const token = localStorage.getItem('accessToken');
  if (!token) {
    return init;
  }
  const headers = new Headers(init.headers ?? undefined);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

export async function authenticatedFetch(
  url: string,
  init: RequestInit = {},
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { allowAuthRetry = true } = options;
  const response = await fetch(url, withAccessTokenHeader(init));

  if (
    response.status !== 401 ||
    !allowAuthRetry ||
    typeof window === 'undefined' ||
    url.includes('/auth/refresh')
  ) {
    return response;
  }

  const newToken = await refreshAccessToken();
  if (!newToken) {
    return response;
  }

  return fetch(url, withAccessTokenHeader(init));
}

/** 长流程（多文件上传、专辑提交）开始前主动续期，降低中途 401 */
export async function ensureSessionBeforeLongTask(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  const hasAccess = !!localStorage.getItem('accessToken');
  const hasRefresh = !!localStorage.getItem('refreshToken');
  if (!hasAccess && !hasRefresh) {
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }
  if (hasRefresh) {
    const token = await refreshAccessToken();
    if (!token) {
      if (!localStorage.getItem('refreshToken')) {
        throw new Error(SESSION_EXPIRED_MESSAGE);
      }
      throw new Error(SESSION_BUSY_MESSAGE);
    }
  }
}

export function userMessageForUploadFailure(
  status: number,
  errBody?: Record<string, unknown>
): string {
  if (status === 401) {
    return SESSION_EXPIRED_MESSAGE;
  }
  if (status === 429) {
    return SESSION_BUSY_MESSAGE;
  }
  if (status === 413) {
    return `单首音频文件过大，请控制在 ${AUDIO_UPLOAD_MAX_LABEL} 以内，或压缩/转 FLAC 后重试`;
  }
  const raw =
    (typeof errBody?.error === 'string' && errBody.error) ||
    (typeof errBody?.message === 'string' && errBody.message) ||
    `HTTP ${status}`;
  return raw;
}
